export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: string,
  ) {
    super(message);
  }
}

export const API = "/api/v1";

export async function api<T = unknown>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const { json, ...rest } = init;
  const res = await fetch(`${API}${path}`, {
    credentials: "same-origin",
    ...rest,
    headers: { ...(json !== undefined ? { "content-type": "application/json" } : {}), ...(rest.headers ?? {}) },
    body: json !== undefined ? JSON.stringify(json) : rest.body,
  });
  const body = res.headers.get("content-type")?.includes("application/json") ? await res.json() : null;
  if (!res.ok) {
    const err = (body as { error?: { code: string; message: string } } | null)?.error;
    if (res.status === 401 && !path.startsWith("/auth/")) window.dispatchEvent(new Event("dl:signed-out"));
    throw new ApiError(err?.message ?? `Request failed (${res.status})`, res.status, err?.code ?? "HTTP_ERROR");
  }
  return body as T;
}

export interface Me {
  id: string;
  email: string;
  name: string;
  role: "staff" | "customer";
  orgId: string | null;
  orgName: string | null;
  tourSeenAt: string | null;
  discordUserId: string | null;
}

export type Status = "open" | "in_progress" | "waiting" | "resolved" | "closed";
export type Kind = "ticket" | "feedback" | "task" | "github_issue";

export interface Item {
  id: string;
  number: number;
  ref: string;
  kind: Kind;
  source: string;
  title: string;
  body: string;
  status: Status;
  category: string;
  orgId: string | null;
  orgName: string | null;
  appName: string | null;
  reporterName: string;
  createdAt: string;
  updatedAt: string;
  resolvedAt: string | null;
  commentCount?: number;
  // Staff only:
  priority?: number | null;
  effort?: number | null;
  score?: number;
  projectId?: string | null;
  projectName?: string | null;
  reporterEmail?: string;
  assigneeId?: string | null;
  assigneeName?: string | null;
  dueAt?: string | null;
  githubRepo?: string | null;
  githubNumber?: number | null;
  githubUrl?: string | null;
  labels?: string[];
  metadata?: Record<string, unknown>;
  needsSorting?: boolean;
  scoreExplained?: { score: number; reasons: string[] };
}
